import React, { useState, useEffect } from 'react';
import discountService from '../../services/discountService';

// Mock products for selection (since we don't have a product API yet)
const MOCK_PRODUCTS = [
    { id: 1, name: 'Starter Pack' },
    { id: 2, name: 'Booster Pack' },
    { id: 3, name: 'Premium Box' },
    { id: 4, name: 'Collector Edition' },
];

export default function DiscountManager() {
    const [discounts, setDiscounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingDiscount, setEditingDiscount] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        type: 'percentage',
        value: '',
        target_type: 'all',
        target_ids: [],
        start_date: '',
        end_date: '',
        usage_limit: '',
        is_active: true,
    });

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [usageHistory, setUsageHistory] = useState([]);
    const [selectedDiscountHistory, setSelectedDiscountHistory] = useState(null);

    useEffect(() => {
        loadDiscounts();
    }, []);

    const loadDiscounts = async () => {
        setLoading(true);
        try {
            const data = await discountService.getDiscounts();
            setDiscounts(data);
        } catch (error) {
            console.error('Error loading discounts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (discount = null) => {
        if (discount) {
            setEditingDiscount(discount);
            setFormData({
                ...discount,
                start_date: discount.start_date ? discount.start_date.split('T')[0] : '',
                end_date: discount.end_date ? discount.end_date.split('T')[0] : '',
            });
        } else {
            setEditingDiscount(null);
            setFormData({
                code: '',
                name: '',
                type: 'percentage',
                value: '',
                target_type: 'all',
                target_ids: [],
                start_date: new Date().toISOString().split('T')[0],
                end_date: '',
                usage_limit: '',
                is_active: true,
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingDiscount(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                value: parseFloat(formData.value),
                usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
            };

            if (editingDiscount) {
                await discountService.updateDiscount(editingDiscount.id, payload);
            } else {
                await discountService.createDiscount(payload);
            }

            handleCloseModal();
            loadDiscounts();
        } catch (error) {
            console.error('Error saving discount:', error);
            alert('Erreur lors de la sauvegarde');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette réduction ?')) {
            try {
                await discountService.deleteDiscount(id);
                loadDiscounts();
            } catch (error) {
                console.error('Error deleting discount:', error);
                alert(`Erreur lors de la suppression: ${error.message}`);
            }
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            await discountService.toggleDiscountStatus(id);
            loadDiscounts();
        } catch (error) {
            console.error('Error toggling status:', error);
        }
    };

    const handleViewHistory = async (discount) => {
        setSelectedDiscountHistory(discount);
        try {
            const history = await discountService.getDiscountUsage(discount.id);
            setUsageHistory(history);
            setShowHistoryModal(true);
        } catch (error) {
            console.error('Error loading history:', error);
        }
    };

    const handleCloseHistoryModal = () => {
        setShowHistoryModal(false);
        setUsageHistory([]);
        setSelectedDiscountHistory(null);
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-center">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight text-gray-900">Réductions</h2>
                    <p className="mt-1 text-sm text-gray-500">Gérez vos codes promo et offres spéciales.</p>
                </div>
                <button
                    className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={() => handleOpenModal()}
                >
                    <span className="text-lg">+</span>
                    <span>Nouvelle Réduction</span>
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {discounts.map(discount => (
                        <div
                            key={discount.id}
                            className={`group relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${!discount.is_active
                                ? 'border-gray-200 bg-gray-50 opacity-75 grayscale'
                                : 'border-gray-200 bg-white shadow-md'
                                }`}
                        >
                            {/* Ticket Header / Value */}
                            <div className={`relative flex h-32 flex-col items-center justify-center p-6 text-white ${!discount.is_active
                                ? 'bg-gray-400'
                                : discount.type === 'percentage'
                                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                                    : 'bg-gradient-to-br from-emerald-500 to-teal-600'
                                }`}>
                                <div className="absolute -bottom-3 left-0 right-0 h-6 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAwIDEyMCIgcHJlc2VydmVBc3BlY3RSYXRpbz0ibm9uZSI+PHBhdGggZD0iTTAgMHY0NmgxMjAwVjBMMCAweiIgZmlsbD0idHJhbnNwYXJlbnQiLz48cGF0aCBkPSJNMCAxMjB2LTQwaDEyMDB2NDBMMCAxMjB6IiBmaWxsPSIjZmZmZmZmIi8+PC9zdmc+')] bg-repeat-x opacity-20"></div>
                                <h3 className="text-4xl font-black tracking-tight">
                                    {discount.type === 'fixed' ? '' : '-'}{discount.value}{discount.type === 'percentage' ? '%' : '€'}
                                </h3>
                                <span className="mt-1 rounded-full bg-white/20 px-3 py-1 text-xs font-medium backdrop-blur-sm">
                                    {discount.code}
                                </span>
                                {/* Decorative circles for ticket effect */}
                                <div className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-gray-50"></div>
                                <div className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-gray-50"></div>
                            </div>

                            {/* Ticket Body */}
                            <div className="flex flex-1 flex-col justify-between p-6">
                                <div>
                                    <h4 className="mb-1 text-lg font-bold text-gray-900">{discount.name}</h4>
                                    <div className="space-y-2 text-sm text-gray-500">
                                        <div className="flex items-center gap-2">
                                            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <span>
                                                {discount.start_date || 'Immédiat'}
                                                {discount.end_date ? ` → ${discount.end_date}` : ''}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                            </svg>
                                            <span>{discount.target_type === 'all' ? 'Tout le magasin' : 'Produits spécifiques'}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            <span>{discount.usage_count} / {discount.usage_limit || '∞'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex items-center justify-between border-t border-dashed border-gray-200 pt-4">
                                    <label className="relative inline-flex cursor-pointer items-center">
                                        <input
                                            type="checkbox"
                                            className="peer sr-only"
                                            checked={discount.is_active}
                                            onChange={() => handleToggleStatus(discount.id)}
                                        />
                                        <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"></div>
                                    </label>

                                    <div className="flex space-x-1">
                                        <button
                                            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                                            onClick={() => handleViewHistory(discount)}
                                            title="Historique"
                                        >
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </button>
                                        <button
                                            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                                            onClick={() => handleOpenModal(discount)}
                                            title="Modifier"
                                        >
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                            </svg>
                                        </button>
                                        <button
                                            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                            onClick={() => handleDelete(discount.id)}
                                            title="Supprimer"
                                        >
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {discounts.length === 0 && (
                        <div className="col-span-full flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-12 text-center">
                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                </svg>
                            </div>
                            <h3 className="mt-2 text-sm font-semibold text-gray-900">Aucune réduction</h3>
                            <p className="mt-1 text-sm text-gray-500">Créez votre première offre promotionnelle.</p>
                            <div className="mt-6">
                                <button
                                    className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                    onClick={() => handleOpenModal()}
                                >
                                    <span className="mr-2">+</span>
                                    Nouvelle Réduction
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/50 p-4 backdrop-blur-sm" onClick={handleCloseModal}>
                    <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl ring-1 ring-gray-900/5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b border-gray-100 p-6">
                            <h3 className="text-xl font-bold text-gray-900">{editingDiscount ? 'Modifier la réduction' : 'Créer une réduction'}</h3>
                            <button className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500" onClick={handleCloseModal}>
                                <span className="sr-only">Fermer</span>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Code Promo *</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="code"
                                            value={formData.code}
                                            onChange={handleInputChange}
                                            placeholder="Ex: SUMMER20"
                                            required
                                            className="block w-full rounded-xl border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 uppercase"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Nom de l'offre</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="Ex: Soldes d'été"
                                            className="block w-full rounded-xl border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Type de réduction</label>
                                    <div className="mt-2">
                                        <select
                                            name="type"
                                            value={formData.type}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-xl border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                        >
                                            <option value="percentage">Pourcentage (%)</option>
                                            <option value="fixed">Montant Fixe (€)</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Valeur *</label>
                                    <div className="mt-2">
                                        <input
                                            type="number"
                                            name="value"
                                            value={formData.value}
                                            onChange={handleInputChange}
                                            placeholder="Ex: 20"
                                            min="0"
                                            step="0.01"
                                            required
                                            className="block w-full rounded-xl border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium leading-6 text-gray-900">Cible</label>
                                <div className="mt-2">
                                    <select
                                        name="target_type"
                                        value={formData.target_type}
                                        onChange={handleInputChange}
                                        className="block w-full rounded-xl border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                    >
                                        <option value="all">Tous les produits</option>
                                        <option value="product">Produits spécifiques</option>
                                    </select>
                                </div>
                            </div>

                            {formData.target_type === 'product' && (
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Sélectionner les produits (Mock)</label>
                                    <div className="mt-2">
                                        <select
                                            multiple
                                            className="block w-full rounded-xl border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 disabled:bg-gray-50 disabled:text-gray-500"
                                            disabled
                                            title="Fonctionnalité à venir avec l'API produits"
                                        >
                                            {MOCK_PRODUCTS.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <p className="mt-2 text-xs text-gray-500">La sélection multiple sera activée avec l'API produits.</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Date de début</label>
                                    <div className="mt-2">
                                        <input
                                            type="date"
                                            name="start_date"
                                            value={formData.start_date}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-xl border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Date de fin (Optionnel)</label>
                                    <div className="mt-2">
                                        <input
                                            type="date"
                                            name="end_date"
                                            value={formData.end_date}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-xl border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium leading-6 text-gray-900">Limite d'utilisation (Optionnel)</label>
                                <div className="mt-2">
                                    <input
                                        type="number"
                                        name="usage_limit"
                                        value={formData.usage_limit}
                                        onChange={handleInputChange}
                                        placeholder="Ex: 100 premiers clients"
                                        min="1"
                                        className="block w-full rounded-xl border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                                <button
                                    type="button"
                                    className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    onClick={handleCloseModal}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="inline-flex justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                >
                                    {editingDiscount ? 'Mettre à jour' : 'Créer la réduction'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showHistoryModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/50 p-4 backdrop-blur-sm" onClick={handleCloseHistoryModal}>
                    <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-900/5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b border-gray-100 p-6">
                            <h3 className="text-xl font-bold text-gray-900">Historique: {selectedDiscountHistory?.code}</h3>
                            <button className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500" onClick={handleCloseHistoryModal}>
                                <span className="sr-only">Fermer</span>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="overflow-hidden rounded-b-2xl">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Client</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Montant économisé</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {usageHistory.map((usage) => (
                                        <tr key={usage.id} className="hover:bg-gray-50">
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{usage.customer}</td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                {new Date(usage.date).toLocaleDateString()} {new Date(usage.date).toLocaleTimeString()}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm font-bold text-green-600">{usage.amount.toFixed(2)} €</td>
                                        </tr>
                                    ))}
                                    {usageHistory.length === 0 && (
                                        <tr>
                                            <td colSpan="3" className="px-6 py-12 text-center text-sm text-gray-500">Aucune utilisation pour le moment</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
